import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { initials, CHANNEL_META, STATUS_META, STRATEGY_LIBRARY, strategyLabel, relativeTime, fullDate, typeLabel, SIMPLE_STATUS, simpleStatus, simpleStatusLabel, LEAD_LABEL } from "@/lib/format";
import { ACTION_TEXT, STAGE_LABEL, PHASE_LABEL, stageTag, dayLabel, currentStep, visibleSteps, lastDone, firstName } from "@/lib/plan";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Sparkles, Loader2 } from "lucide-react";
import "@/styles/prospect-drawer.css";

// v7 · Ficha del lead (maqueta v6): «Siguiente paso» y plan los prepara el motor de siguiente acción
// (backend/lead_plan.py). Pestañas Perfil / Conversación / Historial y pie con estado simplificado.

const SOURCE_LABEL = {
  manual: "Añadido a mano", url_publica: "URL pública", info_copiada: "Información copiada", importado: "Importado",
  publico_tikhub: "Búsqueda", publico_apify: "Búsqueda", instagram_oficial: "Búsqueda", descubrimiento: "Búsqueda",
};
const REACTION_LABEL = {
  respondio_comentario: "Respondió a tu comentario", me_gusta: "Le dio me gusta", me_siguio: "Te siguió de vuelta",
  me_escribio: "Te escribió", respondio: "Respondió", sin_reaccion: "Sin reacción", sin_respuesta: "Sin respuesta",
  ha_aceptado: "Aceptó tu solicitud", interesado: "Mostró interés", cita: "Cita agendada", ahora_no: "Ahora no",
  no_contactar: "No contactar",
};
const EVENT_LABEL = {
  opportunity_saved: "Guardado desde Oportunidades", prospect_analyzed: "Perfil analizado", status_changed: "Cambio de estado",
  strategy_selected: "Estrategia elegida", action_recommended: "Texto preparado por la IA", message_used: "Mensaje enviado",
  reply_suggested: "Sugerencia de respuesta", prospect_replied: "Te respondió", result_registered: "Resultado registrado",
  postponed: "Pospuesto", plan_created: "Plan preparado", plan_recalculated: "Plan recalculado",
};
const PRIO = { HIGH: ["Prioridad alta", "hi"], MEDIUM: ["Prioridad media", "md"], LOW: ["Prioridad baja", ""] };
const DONE_STATUS = new Set(["cliente", "satisfaccion", "promotor", "referido", "conseguido"]);
const WARM = new Set(["comentar", "seguir", "reaccionar_historia", "me_gusta", "responder_historia"]);
const arr = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

function Photo({ p, small }) {
  const [bad, setBad] = useState(false);
  const style = small ? { width: 40, height: 40, fontSize: 13 } : undefined;
  if (p.photo_url && !bad) return <img className="pd-ph" style={style} src={p.photo_url} alt="" referrerPolicy="no-referrer" onError={() => setBad(true)} />;
  return <span className="pd-ph" style={style} aria-hidden>{initials(p.name)}</span>;
}

function agoLabel(days) {
  if (days === null || days === undefined || days < 0) return "—";
  if (days === 0) return "Hoy";
  if (days === 1) return "Ayer";
  if (days < 30) return `Hace ${days} días`;
  const m = Math.round(days / 30);
  return m <= 1 ? "Hace 1 mes" : `Hace ${m} meses`;
}
const daysSince = (ts) => { const t = Date.parse(ts); return Number.isNaN(t) ? null : Math.max(0, Math.floor((Date.now() - t) / 864e5)); };
const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString("es-ES") : "—");

export default function ProspectDrawer({ prospect, open, onOpenChange, onUpdated, onDeleted }) {
  const [tab, setTab] = useState("perfil");
  const [edits, setEdits] = useState({});
  const [respText, setRespText] = useState("");
  const [respWho, setRespWho] = useState("prospecto");
  const [reactSel, setReactSel] = useState("");
  const [reactText, setReactText] = useState("");
  const [notes, setNotes] = useState("");
  const [contact, setContact] = useState("");
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState("");
  const asked = useRef({});

  const pid = prospect?.id;
  const loadEvents = () => {
    if (!pid) return;
    api.get(`/prospects/${pid}/events`).then((r) => setEvents(r.data.events || [])).catch(() => setEvents([]));
  };

  useEffect(() => { if (open) loadEvents(); }, [open, pid]); // eslint-disable-line
  useEffect(() => {
    setTab("perfil"); setEdits({}); setRespText(""); setReactSel(""); setReactText("");
    setNotes(prospect?.notes || ""); setContact("");
  }, [pid]); // eslint-disable-line

  // Contactos sin plan (guardados antes de la v7): se prepara al abrir la ficha, una sola vez.
  useEffect(() => {
    const p = prospect;
    if (!open || !p || p.plan || p.is_demo || p.no_contact || p.status === "no_contactar" || DONE_STATUS.has(p.status)) return;
    if (asked.current[p.id]) return;
    asked.current[p.id] = true;
    run("plan", () => api.post(`/prospects/${p.id}/plan`, { event: "abrir" }), "");
  }, [open, pid]); // eslint-disable-line

  // Plan recién guardado: la IA lo afina en segundo plano; se recoge en cuanto está.
  useEffect(() => {
    if (!open || !prospect?.plan?.pending_ai) return undefined;
    let n = 0;
    const t = setInterval(() => {
      n += 1;
      api.get(`/prospects/${prospect.id}`).then((r) => {
        if (!r.data?.plan?.pending_ai || n >= 8) { clearInterval(t); onUpdated(r.data); }
      }).catch(() => clearInterval(t));
    }, 5000);
    return () => clearInterval(t);
  }, [open, pid, prospect?.plan?.pending_ai]); // eslint-disable-line

  if (!prospect) return null;
  const p = prospect;
  const plan = p.plan && p.plan.steps ? p.plan : null;
  const q = p.qualification || {};
  const status = p.status || "descubierto";
  const noContact = !!p.no_contact || status === "no_contactar";
  const channel = p.channel || p.recommended_channel || "instagram";
  const channelLabel = CHANNEL_META[channel]?.label || "Instagram";
  const handle = p.ig_username || "";
  const profileUrl = p.profile_url || (handle ? `https://instagram.com/${handle}` : "");
  const name1 = firstName(p.name);
  const cur = plan ? currentStep(plan) : null;
  const steps = plan ? visibleSteps(plan) : [];
  const ld = plan ? lastDone(plan) : null;
  const conv = arr(p.conversation);
  const pub = conv.filter((m) => m.kind === "publico");
  const priv = conv.filter((m) => m.kind !== "publico");
  const rs = p.reply_suggestion && Object.keys(p.reply_suggestion).length ? p.reply_suggestion : null;

  async function run(key, fn, msg) {
    if (busy) return null;
    setBusy(key);
    try {
      const r = await fn();
      onUpdated(r.data);
      if (msg) toast.success(msg);
      loadEvents();
      return r.data;
    } catch (err) { toast.error(apiError(err)); return null; } finally { setBusy(""); }
  }
  const patch = (body, msg) => run("patch", () => api.put(`/prospects/${p.id}`, body), msg);
  const react = (reaction, text = "", msg = "Anotado.") =>
    run("react", () => api.post(`/prospects/${p.id}/plan/react`, { reaction, text }), msg).then((d) => { if (d) { setReactSel(""); setReactText(""); } return d; });
  const textOf = (s) => (edits[s.id] !== undefined ? edits[s.id] : s.content || "");
  const markDone = (s) => run("done", () => api.post(`/prospects/${p.id}/plan/done`, { step_id: s.id, content: ACTION_TEXT[s.action] ? textOf(s) : null }),
    "Hecho. El siguiente paso ya está en tu plan.").then((d) => { if (d) setEdits({}); });
  const otherAction = () => run("plan", () => api.post(`/prospects/${p.id}/plan`, { event: "otra" }), "La IA te propone otra acción.");
  const replan = (event = "abrir") => run("plan", () => api.post(`/prospects/${p.id}/plan`, { event }), "Plan actualizado.");
  const postpone = () => run("postpone", () => api.post(`/prospects/${p.id}/plan/postpone`, { days: 1 }), "Pospuesto a mañana.");
  const stepText = (s) => run("text", () => api.post(`/prospects/${p.id}/plan/step-text`, { step_id: s.id }), "Texto preparado.");
  const toggleTodo = (s, i, done) => run("todo", () => api.post(`/prospects/${p.id}/plan/todo`, { step_id: s.id, index: i, done }), "");
  const openTarget = (s) => {
    const txt = ACTION_TEXT[s.action] ? textOf(s) : "";
    if (txt) { navigator.clipboard.writeText(txt); toast.success(`Copiado. Pégalo en ${channelLabel}.`); }
    const url = s.action === "preparar" && p.website ? p.website : (profileUrl || "https://instagram.com/");
    window.open(url.startsWith("http") ? url : `https://${url}`, "_blank", "noopener");
  };
  const addResponse = async () => {
    const t = respText.trim();
    if (!t) { toast.error("Escribe el texto del mensaje."); return; }
    if (respWho === "prospecto") {
      const d = await react("respondio", t, "Anotado. La IA ha recalculado el siguiente paso.");
      if (d) setRespText("");
    } else {
      const d = await run("conv", () => api.post(`/prospects/${p.id}/conversation`, { who: "usuario", text: t, channel, kind: "privado" }), "Mensaje añadido.");
      if (d) setRespText("");
    }
  };
  const helpReply = () => run("help", () => api.post(`/prospects/${p.id}/help-reply`), "Sugerencia lista.");
  const saveNotes = () => { if ((notes || "") !== (p.notes || "")) patch({ notes }, "Notas guardadas."); };
  const saveContact = () => {
    const v = contact.trim();
    if (!v) return;
    patch({ contact_person: { ...(p.contact_person || {}), handle: v } }, "Persona de contacto guardada.").then(() => setContact(""));
  };

  // ---------- Siguiente paso ----------
  const reactionFor = (s) => {
    if (!s || s.reaction) return null;
    if (s.action === "comentar") return { q: "¿Cómo ha reaccionado a tu comentario?", opts: ["respondio_comentario", "me_gusta", "me_siguio", "me_escribio", "sin_reaccion"] };
    if (s.action === "seguir" && p.is_private && !p.follow_accepted) return { q: "¿Ha aceptado tu solicitud?", opts: ["ha_aceptado", "sin_reaccion"] };
    if (WARM.has(s.action)) return { q: "¿Ha reaccionado?", opts: ["me_gusta", "me_siguio", "me_escribio", "sin_reaccion"] };
    return null;
  };
  const askReaction = ld && cur && cur.trigger !== "evento" ? reactionFor(ld) : (ld && !cur ? reactionFor(ld) : null);
  const needsText = (r) => ["respondio_comentario", "me_escribio"].includes(r);

  let nextCard;
  if (noContact) {
    nextCard = <><span className="pd-k">No contactar</span><h3>No preparamos más acciones</h3><p className="pd-lead">Marcaste este contacto como «No contactar».</p></>;
  } else if (DONE_STATUS.has(status) && !cur) {
    nextCard = <><span className="pd-k">Conseguido</span><h3>¡Bien hecho!</h3><p className="pd-lead">Cuida la relación y, cuando encaje, pide una recomendación.</p>
      {!p.is_demo && <div className="pd-acts"><button className="pd-btn pd-ghost" onClick={() => replan("abrir")} disabled={!!busy}>Preparar plan para cuidarle</button></div>}</>;
  } else if (!plan) {
    nextCard = <><span className="pd-k">Siguiente paso</span><h3>Preparando su plan…</h3>
      <div className="pd-busy"><Loader2 size={15} className="animate-spin" /> La IA está revisando su perfil y lo que ya ha pasado.</div>
      {!busy && !p.is_demo && <div className="pd-acts"><button className="pd-btn pd-gold" onClick={() => replan("abrir")}><Sparkles size={15} /> Preparar plan</button></div>}</>;
  } else if (!cur) {
    nextCard = <><span className="pd-k">Plan completado</span><h3>¿Cómo va con {name1}?</h3>
      <p className="pd-lead">No quedan pasos pendientes. Cuéntale a la IA qué ha pasado o pídele un plan nuevo.</p>
      <div className="pd-acts"><button className="pd-btn pd-gold" onClick={() => replan("abrir")} disabled={!!busy}>{busy === "plan" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Recalcular plan</button>
        <button className="pd-link" onClick={() => setTab("conv")}>Registrar cómo va</button></div></>;
  } else {
    const isEvent = cur.trigger === "evento";
    const doneN = steps.filter((s) => s.status === "done").length;
    const k = isEvent ? `Esperando · ${cur.condition || "su respuesta"}` : `${dayLabel(cur.when)} · ${STAGE_LABEL[cur.stage] || ""}`;
    const txtKind = ACTION_TEXT[cur.action];
    const val = textOf(cur);
    const waitingReply = isEvent && ["conversar", "proponer", "cerrar"].includes(cur.stage);
    nextCard = <>
      <div className="pd-top"><span className="pd-k">{k}</span><span className="pd-steps">{steps.map((s, i) => <i key={s.id} className={i < doneN + 1 ? "on" : ""} />)}</span></div>
      <h3>{cur.title}</h3>
      {cur.why && <p className="pd-lead">{cur.why}</p>}
      {askReaction && (
        <div className="pd-react" data-testid="plan-reaction">
          <p className="rq">{askReaction.q}</p>
          <div className="pd-seg">{askReaction.opts.map((o) => (
            <button key={o} className={reactSel === o ? "sel" : ""} disabled={!!busy}
              onClick={() => (needsText(o) ? setReactSel(o) : react(o, "", o === "sin_reaccion" ? "Anotado. Seguimos con el plan." : "Anotado."))}>{REACTION_LABEL[o]}</button>))}</div>
          {needsText(reactSel) && <div className="pd-inrow">
            <input className="pd-in" placeholder="Pega lo que te ha escrito" value={reactText} onChange={(e) => setReactText(e.target.value)} />
            <button className="pd-btn pd-ghost" disabled={!!busy || !reactText.trim()} onClick={() => react(reactSel, reactText.trim(), "Anotado. La IA ha recalculado el plan.")}>Guardar</button></div>}
          <p className="ai">✦ Si responde o te escribe, la IA adelanta el siguiente paso y lo escribe siguiendo esa conversación.</p>
        </div>
      )}
      {arr(cur.todo).length > 0 && <ul className="pd-todo">{cur.todo.map((t, i) => (
        <li key={i}><input type="checkbox" checked={!!t.done} disabled={!!busy || p.is_demo} onChange={(e) => toggleTodo(cur, i, e.target.checked)} />
          <div>{t.text}{t.note && <small>{t.note}</small>}</div></li>))}</ul>}
      {txtKind && (val
        ? <textarea className="pd-msg" value={val} onChange={(e) => setEdits((m) => ({ ...m, [cur.id]: e.target.value }))} data-testid="next-message" />
        : <div className="pd-acts" style={{ marginTop: 4 }}><button className="pd-btn pd-ghost" onClick={() => stepText(cur)} disabled={!!busy}>
          {busy === "text" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Preparar el texto</button></div>)}
      {isEvent ? (
        <div className="pd-acts">
          {waitingReply
            ? <button className="pd-btn pd-gold" onClick={() => { setRespWho("prospecto"); setTab("conv"); }}>Ha respondido</button>
            : p.is_private && !p.follow_accepted
              ? <button className="pd-btn pd-gold" onClick={() => react("ha_aceptado", "", "Anotado. La IA prepara el plan.")} disabled={!!busy}>Ha aceptado</button>
              : <button className="pd-btn pd-gold" onClick={() => markDone(cur)} disabled={!!busy}>✓ Hecho</button>}
          <button className="pd-link" onClick={otherAction} disabled={!!busy}>Otra acción</button>
        </div>
      ) : (
        <div className="pd-acts">
          <button className="pd-btn pd-gold" onClick={() => openTarget(cur)} data-testid="next-copy-open">
            {txtKind ? `Copiar y abrir ${channelLabel} ↗` : cur.action === "preparar" && p.website ? "Abrir su web ↗" : "Abrir su perfil ↗"}</button>
          <button className="pd-btn pd-ghost" onClick={() => markDone(cur)} disabled={!!busy} data-testid="next-done">{busy === "done" ? "Guardando…" : "✓ Hecho"}</button>
          <button className="pd-link" onClick={otherAction} disabled={!!busy} data-testid="next-other">{busy === "plan" ? "Pensando…" : "Otra acción"}</button>
          <button className="pd-link" onClick={postpone} disabled={!!busy}>Posponer</button>
        </div>
      )}
      {(cur.hint || plan.pending_ai) && <p className="pd-hint">{plan.pending_ai ? "✦ La IA está afinando este plan con su perfil; se actualizará en unos segundos." : cur.hint}</p>}
      {plan.ai_error && <p className="pd-hint">La IA no respondió y este plan se ha hecho con las reglas básicas. <button className="pd-link" onClick={() => replan("abrir")} disabled={!!busy}>Reintentar con IA</button></p>}
    </>;
  }

  // ---------- Perfil ----------
  const v = plan?.verdict || {};
  const verdictLvl = v.level || ((p.fit_score || 0) >= 60 ? "ok" : "warn");
  const verdictTitle = v.title || (p.fit_score ? `Encaja · ${p.fit_score}/100` : "Por valorar");
  const verdictText = v.text || q.why_shown || p.fit_reason || "";
  const posts = arr(p.posts).length ? p.posts : arr(p.recent_posts).map((c) => ({ caption: c }));
  const known = arr(plan?.known).length ? plan.known : arr(q.observed_facts).map((t) => ({ text: t, source: "búsqueda" }));
  const guess = arr(plan?.guess).length ? plan.guess : arr(q.inferences);
  const confirm = arr(plan?.confirm).length ? plan.confirm : (arr(q.unknowns).length ? q.unknowns : arr(p.missing_info));
  const activity = p.recent_activity_days;
  const actPct = activity === null || activity === undefined ? null : activity <= 7 ? 95 : activity <= 30 ? 80 : activity <= 90 ? 60 : 30;
  const fitBars = [["Perfil", plan?.fit?.perfil], ["Necesidad", plan?.fit?.necesidad], ["Actividad", actPct]].filter(([, x]) => typeof x === "number");
  const facts = [
    [fmtNum(p.followers), "seguidores"],
    [agoLabel(activity), "última publicación"],
    [p.posts_per_week ? `~${String(p.posts_per_week).replace(".", ",")}/semana` : "—", "publicaciones"],
    [p.location || "—", "ubicación"],
    [p.category || (p.entity === "empresa" ? "Negocio" : "—"), "categoría"],
    [p.website ? "Web" : "Sin web", "enlace en la bio"],
  ].filter(([b], i) => b !== "—" && !(i === 5 && !p.website && !p.posts_per_week && !p.followers));
  const strategyVal = p.chosen_strategy || p.strategy || "";
  const recStrategy = plan?.strategy || p.recommended_strategy || "";
  const prio = PRIO[String(p.priority || "").toUpperCase()];
  const geoOk = p.geo_status === "CONFIRMED_COMPATIBLE" || p.location_status === "compatible" || p.location_status === "confirmada";
  const forType = typeLabel(p.objective || p.opportunity_type);
  const phase = p.plan_phase || plan?.phase;
  const isCompany = p.entity === "empresa" || String(q.entity_type || "").toUpperCase() === "COMPANY";
  const apSections = ["warm", "topics", "questions", "avoid"].filter((k) => arr(plan?.[k]).length > 0);
  const num = (k) => apSections.indexOf(k) + 1;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-hidden p-0 sm:max-w-[520px]" data-testid="prospect-drawer">
        <SheetTitle className="sr-only">Ficha de {p.name}</SheetTitle>
        <SheetDescription className="sr-only">Siguiente paso, plan, perfil, conversación e historial del contacto.</SheetDescription>
        <div className="pd">
          <div className="pd-head">
            <div className="pd-who">
              <Photo p={p} />
              <div className="min-w-0">
                <h2>{p.name}</h2>
                {profileUrl
                  ? <a href={profileUrl} target="_blank" rel="noreferrer" data-testid="drawer-profile-link">{handle ? `@${handle}` : "Ver perfil"} ↗</a>
                  : <span className="pd-sub">{p.company || channelLabel}</span>}
              </div>
            </div>
            <div className="pd-chips">
              <span className="pd-chip st" data-testid="drawer-status-chip"><i />{simpleStatusLabel(status)}</span>
              {phase && PHASE_LABEL[phase] && !noContact && <span className="pd-chip md">{PHASE_LABEL[phase]}</span>}
              {LEAD_LABEL[p.lead_label] && <span className="pd-chip md" title={p.label_reason || ""} data-testid="drawer-lead-label">{LEAD_LABEL[p.lead_label].emoji} {LEAD_LABEL[p.lead_label].label}</span>}
              {prio && <span className={`pd-chip ${prio[1]}`}>{prio[0]}</span>}
              {isCompany && <span className="pd-chip">🏢 Empresa</span>}
              {p.is_private && <span className="pd-chip">🔒 Cuenta privada</span>}
              {p.location && <span className="pd-chip">📍 {p.location}{geoOk ? " · compatible" : ""}</span>}
              {forType && <span className="pd-chip">Para: {forType.toLowerCase()}</span>}
              {q.other_brand && <span className="pd-chip md">Trabaja con otra marca: {q.other_brand}</span>}
            </div>
          </div>

          <div className="pd-scroll">
            <section className="pd-next" aria-label="Siguiente paso" data-testid="next-step-card">{nextCard}</section>

            {plan && steps.length > 0 && (
              <section className="pd-plan" aria-label="Plan" data-testid="plan-timeline">
                <h4>Plan con {name1} <small>la IA lo ajusta con cada respuesta</small></h4>
                <div className="pd-pl">
                  {steps.map((s) => {
                    const cls = s.status === "done" ? "done" : s === cur ? "now" : s.trigger !== "fecha" ? "if" : "";
                    const when = s.status === "done"
                      ? `Hecho${s.done_at ? ` · ${relativeTime(s.done_at)}` : ""}${s.reaction ? ` · ${REACTION_LABEL[s.reaction]?.toLowerCase()}` : ""}`
                      : s.when ? `${dayLabel(s.when, { long: true })}${s.content ? " · texto ya preparado" : ""}` : (s.condition || "Cuando ocurra");
                    return <div key={s.id} className={cls}>{s.title}<span className={`pd-tag ${stageTag(s.stage)}`}>{STAGE_LABEL[s.stage] || ""}</span><small>{when}</small></div>;
                  })}
                </div>
              </section>
            )}

            <div className="pd-tabs" role="tablist">
              {[["perfil", "Perfil"], ["conv", "Conversación"], ["hist", "Historial"]].map(([k, l]) => (
                <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`drawer-tab-${k}`}>{l}</button>
              ))}
            </div>

            {tab === "perfil" && (
              <div role="tabpanel">
                {verdictText && <div className={`pd-verdict ${verdictLvl}`}><span className="dot" /><div><b>{verdictTitle}</b><p>{verdictText}</p></div></div>}
                {facts.length >= 2 && <div className="pd-facts">{facts.map(([b, s]) => <div key={s} className="pd-fact"><b title={b}>{b}</b><small>{s}</small></div>)}</div>}

                {isCompany && (
                  <div className="pd-blk"><h4>Persona de contacto <small>quién decide</small></h4>
                    {p.contact_person?.handle
                      ? <div className="pd-pc"><span className="pd-ph" style={{ width: 40, height: 40, fontSize: 13 }}>{initials(p.contact_person.name || p.contact_person.handle)}</span>
                        <div><b>{p.contact_person.name || p.contact_person.handle}</b><small>{p.contact_person.handle}</small></div></div>
                      : <p className="pd-p">Aún sin identificar. Si encuentras a quien dirige el negocio, guarda aquí su cuenta.</p>}
                    {!p.is_demo && <div className="pd-inrow"><input className="pd-in" placeholder="Pega su cuenta personal (Instagram o LinkedIn)" value={contact} onChange={(e) => setContact(e.target.value)} />
                      <button className="pd-btn pd-ghost" onClick={saveContact} disabled={!contact.trim() || !!busy}>Guardar</button></div>}
                  </div>
                )}

                {known.length > 0 && <div className="pd-blk"><h4>Lo que sabemos <small>con su fuente</small></h4>
                  <ul className="pd-list ok">{known.map((k, i) => <li key={i}><b>✓</b>{k.text}{k.source && <span className="src">{k.source}</span>}</li>)}</ul></div>}
                {guess.length > 0 && <div className="pd-blk"><h4>Lo que suponemos <small>deducción de la IA</small></h4>
                  <ul className="pd-list inf">{guess.map((t, i) => <li key={i}><b>≈</b>{t}</li>)}</ul></div>}
                {confirm.length > 0 && <div className="pd-blk"><h4>Por confirmar al hablar</h4>
                  <ul className="pd-list q">{confirm.map((t, i) => <li key={i}><b>?</b>{t}</li>)}</ul></div>}

                <div className="pd-blk"><h4>Sus últimas publicaciones <small>para comentar con algo real</small></h4>
                  {p.is_private && posts.length === 0
                    ? <p className="pd-p">🔒 No visibles: es una cuenta privada. Aparecerán cuando acepte tu solicitud.</p>
                    : posts.length === 0 ? <p className="pd-p">No tenemos sus publicaciones. Ábrelas en su perfil.</p>
                      : <div className="pd-posts">{posts.slice(0, 3).map((x, i) => {
                        const d = x.ts ? daysSince(x.ts) : null;
                        const meta = [d !== null ? agoLabel(d) : "", typeof x.likes === "number" && x.likes >= 0 ? `${fmtNum(x.likes)} ❤` : ""].filter(Boolean).join(" · ");
                        const body = <><div className="pd-ptype"><span>{x.type || "Publicación"}</span>{x.url && <span>↗</span>}</div><p>{x.caption || "Sin texto"}</p>{meta && <small>{meta}</small>}{plan?.best_post === i && <span className="pd-badge">Ideal para comentar</span>}</>;
                        return x.url ? <a key={i} className="pd-post" href={x.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>{body}</a> : <div key={i} className="pd-post">{body}</div>;
                      })}</div>}
                </div>

                {plan && (arr(plan.warm).length > 0 || arr(plan.topics).length > 0 || arr(plan.questions).length > 0 || arr(plan.avoid).length > 0) && (
                  <div className="pd-blk"><h4>Cómo abordarle <small>lo usa la IA para el plan</small></h4>
                    {arr(plan.warm).length > 0 && <><h5>{num("warm")} · {plan.warm_title || "Calentamiento antes de escribir"}</h5>
                      <div className="pd-warm">{plan.warm.map((w, i) => <div key={i}><em>{i + 1}</em><span>{w.title}{w.detail && <small>{w.detail}</small>}{w.quote && <div className="pd-quote">{w.quote}</div>}</span></div>)}</div></>}
                    {arr(plan.topics).length > 0 && <><h5>{num("topics")} · Temas para conversar</h5><div className="pd-topics">{plan.topics.map((t) => <span key={t}>{t}</span>)}</div></>}
                    {arr(plan.questions).length > 0 && <><h5>{num("questions")} · {plan.questions_title || "Preguntas para descubrir si lo necesita"}</h5>
                      <ul className="pd-list q">{plan.questions.map((t, i) => <li key={i}><b>?</b>{t}</li>)}</ul></>}
                    {arr(plan.avoid).length > 0 && <><h5>{num("avoid")} · Qué evitar</h5><ul className="pd-list no">{plan.avoid.map((t, i) => <li key={i}><b>–</b>{t}</li>)}</ul></>}
                  </div>
                )}

                <div className="pd-blk"><h4>Encaje <small>{p.fit_score ? `${p.fit_score}/100` : ""}</small></h4>
                  {fitBars.length > 0
                    ? fitBars.map(([l, x]) => <div key={l} className="pd-fitbar">{l}<i><b style={{ width: `${x}%` }} /></i><span>{x}%</span></div>)
                    : <p className="pd-p">Sin datos suficientes para desglosarlo.</p>}
                  {p.score_breakdown && Object.keys(p.score_breakdown).length > 0 && (
                    <details><summary>Ver el detalle de la puntuación</summary>
                      <p>{Object.entries(p.score_breakdown).map(([k2, x]) => `${k2.replace(/_/g, " ")}: ${x}`).join(" · ")}</p></details>)}
                </div>

                <div className="pd-blk"><h4>Estrategia <small>la usa la IA para preparar cada paso</small></h4>
                  <select className="pd-select" value={strategyVal} disabled={p.is_demo} onChange={(e) => e.target.value && patch({ chosen_strategy: e.target.value, strategy: e.target.value }, "Estrategia guardada.")} data-testid="drawer-strategy-select">
                    <option value="">{recStrategy ? `${strategyLabel(recStrategy)} (recomendada)` : "Elegir estrategia…"}</option>
                    {STRATEGY_LIBRARY.map((s) => <option key={s.id} value={s.id}>{s.label}{recStrategy === s.id ? " (recomendada)" : ""}</option>)}
                  </select>
                  {plan?.strategy_why && <p className="pd-hint">Por qué: {plan.strategy_why}</p>}
                </div>
                {p.bio_public && <div className="pd-blk"><h4>Bio de {channelLabel}</h4><p className="pd-p">{p.bio_public}</p></div>}
                <p className="pd-hint">Origen: {SOURCE_LABEL[p.source] || SOURCE_LABEL[p.discovery_method] || "Añadido a mano"}{p.query ? ` · búsqueda «${p.query}»` : ""}. CaptAcción usa solo información pública o la que tú aportas.</p>
              </div>
            )}

            {tab === "conv" && (
              <div role="tabpanel">
                {pub.length > 0 && <div className="pd-blk"><h4>Comentarios públicos <small>en sus publicaciones</small></h4>
                  {pub.map((m, i) => <div key={i} className={`pd-bub ${m.who === "prospecto" ? "them" : "me"}`}>{m.text}<small>{m.who === "prospecto" ? name1 : "Tú"}{m.date ? ` · ${relativeTime(m.date)}` : ""}</small></div>)}</div>}
                <div className="pd-blk">
                  <h4>{pub.length ? "Mensajes privados" : "Conversación"} <small>{channelLabel}</small></h4>
                  {priv.length === 0 && <p className="pd-empty">Aún no hay mensajes privados.{cur && ACTION_TEXT[cur.action] ? " El siguiente está arriba, listo para copiar." : ""}</p>}
                  <div data-testid="conversation-list">
                    {priv.map((m, i) => <div key={i} className={`pd-bub ${m.who === "prospecto" ? "them" : "me"}`}>{m.text}<small>{m.who === "prospecto" ? name1 : "Tú"}{m.date ? ` · ${relativeTime(m.date)}` : ""}</small></div>)}
                  </div>
                  {!p.is_demo && !noContact && <>
                    <div className="pd-seg pd-who-toggle">
                      <button className={respWho === "prospecto" ? "sel" : ""} onClick={() => setRespWho("prospecto")} data-testid="response-who-them">Me ha respondido</button>
                      <button className={respWho === "usuario" ? "sel" : ""} onClick={() => setRespWho("usuario")} data-testid="response-who-me">Le he escrito</button>
                    </div>
                    <div className="pd-inrow">
                      <input className="pd-in" placeholder={respWho === "prospecto" ? "Pega aquí lo que te ha respondido" : "Pega aquí lo que le has escrito"} value={respText}
                        onChange={(e) => setRespText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addResponse()} data-testid="response-input" />
                      <button className="pd-btn pd-ghost" onClick={addResponse} disabled={!!busy} data-testid="response-save">Añadir</button>
                    </div>
                    {respWho === "prospecto" && <p className="pd-hint">Al añadir su respuesta, la IA recalcula el plan y te prepara qué contestar.</p>}
                    {priv.length > 0 && priv[priv.length - 1].who === "prospecto" && (
                      <div className="pd-acts"><button className="pd-btn pd-ghost" onClick={helpReply} disabled={!!busy}>
                        {busy === "help" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Ayúdame a responder</button></div>)}
                    {rs && <Suggestion rs={rs} />}
                  </>}
                </div>
                {!p.is_demo && !noContact && (
                  <div className="pd-blk">
                    <h4>¿Cómo va? <small>la IA recalcula el plan</small></h4>
                    <div className="pd-seg" data-testid="drawer-result-seg">
                      {p.is_private && !p.follow_accepted && <button onClick={() => react("ha_aceptado", "", "Anotado. La IA prepara el plan.")} disabled={!!busy}>Ha aceptado</button>}
                      <button onClick={() => { setRespWho("prospecto"); setTab("conv"); }} disabled={!!busy}>Ha respondido</button>
                      <button onClick={() => react("sin_respuesta", "", "Anotado. Si no responde, el plan sigue con el seguimiento.")} disabled={!!busy}>Sin respuesta</button>
                      <button onClick={() => react("interesado", "", "Anotado. La IA prepara la propuesta.")} disabled={!!busy}>Interesado</button>
                      <button onClick={() => react("cita", "", "Anotado. La IA prepara la reunión y el seguimiento.")} disabled={!!busy}>Cita agendada</button>
                      <button onClick={() => react("ahora_no", "", "Anotado. Lo retomamos en 3 meses.")} disabled={!!busy}>Ahora no</button>
                      <button onClick={() => react("no_contactar", "", "Anotado: no se le volverá a contactar.")} disabled={!!busy}>No contactar</button>
                    </div>
                    {busy === "react" && <div className="pd-busy"><Loader2 size={14} className="animate-spin" /> Recalculando el plan…</div>}
                  </div>
                )}
              </div>
            )}

            {tab === "hist" && (
              <div role="tabpanel">
                <div className="pd-blk"><h4>Historial</h4>
                  <div className="pd-tl" data-testid="prospect-timeline">
                    {[...events].reverse().map((e, i) => {
                      const md = e.metadata || {};
                      let label = EVENT_LABEL[e.event_type] || e.event_type;
                      if (e.event_type === "plan_step_done") label = md.title || "Paso hecho";
                      else if (e.event_type === "plan_reaction") label = REACTION_LABEL[md.reaction] || "Reacción";
                      else if (e.event_type === "status_changed") label = `${label} · ${STATUS_META[md.from]?.label || md.from || "—"} → ${STATUS_META[md.to]?.label || md.to || "—"}`;
                      else if (e.event_type === "strategy_selected") label = `${label} · ${strategyLabel(md.strategy) || md.strategy || ""}`;
                      return <div key={e.event_id || i}>{label}<small>{relativeTime(e.ts)}</small></div>;
                    })}
                    <div>Añadido a tu CRM<small>{fullDate(p.created_at)}{p.query ? ` · búsqueda «${p.query}»` : ""}</small></div>
                  </div>
                </div>
                <div className="pd-blk"><h4>Notas</h4>
                  <textarea className="pd-notes" placeholder="Apunta lo que quieras recordar de este contacto" value={notes}
                    onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} data-testid="drawer-notes" />
                </div>
              </div>
            )}
          </div>

          <div className="pd-foot">
            <label htmlFor="pd-status">Estado</label>
            <select id="pd-status" value={simpleStatus(status)} disabled={p.is_demo} onChange={(e) => patch({ status: e.target.value }, "Estado actualizado.")} data-testid="drawer-status-select">
              {SIMPLE_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="pd-del" data-testid="drawer-delete-button"><Trash2 size={15} /> Eliminar</button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar a {p.name}?</AlertDialogTitle>
                  <AlertDialogDescription>No volverá a aparecer en tus búsquedas. Si ya has conseguido tu objetivo con esta persona, mejor márcala como «Objetivo conseguido» en Estado: así cuenta en tus métricas y conservas su historial. Podrás deshacer la eliminación durante unos segundos.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => { onOpenChange(false); onDeleted(p); }} data-testid="confirm-delete-prospect">Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Suggestion({ rs }) {
  return (
    <div className="pd-sugg" data-testid="reply-suggestion">
      {rs.whats_happening && <p><b>Qué ocurre:</b> {rs.whats_happening}</p>}
      {rs.objective_now && <p><b>Objetivo ahora:</b> {rs.objective_now}</p>}
      {rs.reply && <div><b>Respuesta sugerida:</b><p className="pd-p" style={{ marginTop: 4 }}>{rs.reply}</p>
        <button className="pd-link" onClick={() => { navigator.clipboard.writeText(rs.reply); toast.success("Copiado."); }}>Copiar</button></div>}
      {rs.reply_short && <p><b>Versión breve:</b> {rs.reply_short}</p>}
      {rs.warning && <p style={{ color: "#F1707A" }}>{rs.warning}</p>}
    </div>
  );
}
